/**
 * ScamSmart Creator/Owner instant-access route.
 *
 * Visit: /scamsmart/unlock?token=<SCAMSMART_BYPASS_TOKEN>&email=<your-email>
 * Effect: grants paid access for that email (writes purchase record) +
 *         stores email in localStorage + sends welcome email +
 *         redirects to /scamsmart/dashboard.
 *
 * One-click — no form filling. Bookmark it on any device.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { setStoredScamSmartEmail } from '../components/ScamSmartGuard';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;

const ScamSmartUnlock = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: 'working', message: '' });

  useEffect(() => {
    const token = params.get('token');
    const email = params.get('email');
    if (!token || !email) {
      setState({ phase: 'error', message: 'Missing token or email in URL.' });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/scamsmart/dev/grant-access`, { email, token });
        if (cancelled) return;
        setStoredScamSmartEmail(email);
        setState({ phase: 'done', message: email });
        setTimeout(() => { if (!cancelled) navigate('/scamsmart/dashboard'); }, 1100);
      } catch (e) {
        const msg = e?.response?.data?.detail || 'Could not unlock — check your token.';
        if (!cancelled) setState({ phase: 'error', message: msg });
      }
    })();
    return () => { cancelled = true; };
  }, [params, navigate]);

  return (
    <div className="ss-page" data-testid="ss-unlock-page">
      <div className="ss-test-wrap" style={{ display: 'flex', alignItems: 'center', minHeight: '80vh' }}>
        <div className="ss-test-container" style={{ width: '100%' }}>
          <div className="ss-preview-card" style={{ textAlign: 'center' }}>
            {state.phase === 'working' && (
              <>
                <Loader2 size={42} color="#16B1C4" style={{ margin: '0 auto 18px', animation: 'ss-spin 1s linear infinite' }} />
                <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>
                  Unlocking ScamSmart…
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, margin: 0 }}>
                  Granting creator access and preparing your dashboard.
                </p>
              </>
            )}
            {state.phase === 'done' && (
              <>
                <ShieldCheck size={48} color="#16B1C4" strokeWidth={1.5} style={{ margin: '0 auto 16px' }} />
                <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>
                  You're in
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: 0 }}>
                  Access granted for <strong style={{ color: '#16B1C4' }}>{state.message}</strong>. Redirecting…
                </p>
              </>
            )}
            {state.phase === 'error' && (
              <>
                <AlertTriangle size={42} color="#F9B03E" style={{ margin: '0 auto 16px' }} />
                <h1 style={{ color: '#fff', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>
                  Could not unlock
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, margin: '0 0 18px' }}>
                  {state.message}
                </p>
                <Link to="/scamsmart" className="ss-btn-primary" style={{ textDecoration: 'none' }}>
                  Back to ScamSmart
                </Link>
              </>
            )}
          </div>
          <style>{`@keyframes ss-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
      </div>
    </div>
  );
};

export default ScamSmartUnlock;
